import React, { useMemo } from 'react';
import { Empty } from 'antd';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import type { SupplyDemandData } from '../types';
import {
  aggregateSupplyByCountry,
  aggregateDemandByApplication,
  calculateSupplyDemandTrend,
} from '../utils/supplyDemandUtils';

interface SupplyDemandChartsProps {
  data: SupplyDemandData[];
}

/**
 * 供需分析图表组件 (ECharts版)
 * 
 * 功能：
 * - 产量排名柱状图
 * - 供需对比折线图 (双轴)
 * - 需求分布饼图
 */
const SupplyDemandCharts: React.FC<SupplyDemandChartsProps> = ({ data }) => {
  if (!data || data.length === 0) {
    return <Empty description="暂无供需数据" />;
  }

  // 1. 产量排名数据 (取前10)
  const productionRankingData = useMemo(() => {
    return aggregateSupplyByCountry(data).slice(0, 10);
  }, [data]);

  // 2. 供需趋势数据
  const trendData = useMemo(() => {
    return calculateSupplyDemandTrend(data);
  }, [data]);

  // 3. 需求分布数据
  const demandDistributionData = useMemo(() => {
    const demandByApp = aggregateDemandByApplication(data);
    return Object.entries(demandByApp).map(([app, demand]) => ({
      name: app,
      value: Math.round(demand),
    }));
  }, [data]);

  // --- ECharts 配置 ---

  // 1. 产量排名柱状图配置
  const productionOption: EChartsOption = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      textStyle: { color: '#333' }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: productionRankingData.map(item => item.country),
      axisLabel: { color: '#666' },
      axisLine: { lineStyle: { color: '#eee' } }
    },
    yAxis: {
      type: 'value',
      name: '产量 (吨)',
      nameTextStyle: { color: '#666' },
      axisLabel: {
        color: '#666',
        formatter: (value: number) => `${(value / 1000).toFixed(0)}K`
      },
      splitLine: { lineStyle: { type: 'dashed', color: '#eee' } }
    },
    series: [
      {
        name: '产量',
        type: 'bar',
        data: productionRankingData.map(item => item.supply),
        itemStyle: {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: '#1890ff' }, // Blue
              { offset: 1, color: '#096dd9' }
            ]
          },
          borderRadius: [4, 4, 0, 0]
        },
        label: {
          show: true,
          position: 'top',
          color: '#666',
          formatter: (params: any) => `${(params.value / 1000).toFixed(1)}K`
        }
      }
    ]
  };

  // 2. 供需决策双轴图配置
  const decisionOption: EChartsOption = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross' },
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      textStyle: { color: '#333' }
    },
    legend: {
      data: ['供应量', '需求量', '供需缺口'],
      textStyle: { color: '#666' }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: trendData.map(item => item.year),
      axisPointer: { type: 'shadow' },
      axisLabel: { color: '#666' },
      axisLine: { lineStyle: { color: '#eee' } }
    },
    yAxis: [
      {
        type: 'value',
        name: '数量 (吨)',
        nameTextStyle: { color: '#666' },
        axisLabel: {
          color: '#666',
          formatter: (value: number) => `${(value / 1000).toFixed(0)}K`
        },
        splitLine: { lineStyle: { type: 'dashed', color: '#eee' } }
      },
      {
        type: 'value',
        name: '缺口 (吨)',
        nameTextStyle: { color: '#ff4d4f' },
        axisLabel: {
          color: '#ff4d4f',
          formatter: (value: number) => `${(value / 1000).toFixed(0)}K`
        },
        splitLine: { show: false }
      }
    ],
    series: [
      {
        name: '供应量',
        type: 'bar',
        data: trendData.map(item => item.supply),
        itemStyle: { color: '#1890ff' },
        barGap: 0
      },
      {
        name: '需求量',
        type: 'bar',
        data: trendData.map(item => item.demand),
        itemStyle: { color: '#52c41a' }
      },
      {
        name: '供需缺口',
        type: 'line',
        yAxisIndex: 1,
        data: trendData.map(item => item.gap),
        itemStyle: { color: '#ff4d4f' },
        lineStyle: { width: 3, type: 'dashed' },
        symbol: 'circle',
        symbolSize: 6
      }
    ]
  };

  // 3. 需求分布饼图配置
  const demandOption: EChartsOption = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'item',
      formatter: '{b}: {c}吨 ({d}%)',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      textStyle: { color: '#333' }
    },
    legend: {
      orient: 'vertical',
      left: 'left',
      textStyle: { color: '#666' }
    },
    series: [
      {
        name: '需求分布',
        type: 'pie',
        radius: ['40%', '70%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 10,
          borderColor: '#fff',
          borderWidth: 2
        },
        label: {
          show: false,
          position: 'center'
        },
        emphasis: {
          label: {
            show: true,
            fontSize: 20,
            fontWeight: 'bold',
            color: '#333'
          }
        },
        labelLine: {
          show: false
        },
        data: demandDistributionData,
        color: ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#096dd9']
      }
    ]
  };

  return (
    <>
      {/* 产量排名柱状图 */}
      <div className="glass-card">
        <h3 style={{ color: '#1890ff', marginBottom: 16 }}>全球主要生产国产量排名（前10）</h3>
        <ReactECharts option={productionOption} style={{ height: 300 }} opts={{ renderer: 'canvas' }} />
      </div>

      {/* 供需决策双轴图 */}
      <div className="glass-card">
        <h3 style={{ color: '#1890ff', marginBottom: 16 }}>供需决策分析 (供应 vs 需求 vs 缺口)</h3>
        <ReactECharts option={decisionOption} style={{ height: 350 }} />
      </div>

      {/* 需求分布饼图 */}
      <div className="glass-card">
        <h3 style={{ color: '#1890ff', marginBottom: 16 }}>钒需求按应用领域分布</h3>
        <ReactECharts option={demandOption} style={{ height: 350 }} />
      </div>
    </>
  );
};

export default SupplyDemandCharts;
